import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sprout, Users, BookOpen, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const EarthkeepersPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-800 to-teal-600 text-white py-24">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_content-upgrade-11/artifacts/fp4ohpdy_EK.jpeg)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Earthkeepers
            </h1>
            <p className="text-2xl md:text-3xl mb-4 text-green-100">
              Environmental Education & Climate Resilience
            </p>
            <p className="text-xl text-green-50 leading-relaxed">
              Empowering children and elders through storytelling and traditional knowledge
            </p>
          </div>
        </div>
      </section>

      {/* Project Report Release */}
      <section className="py-16 bg-green-50 border-b-4 border-green-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                <span className="text-sm font-bold text-green-600 uppercase tracking-wider">Report Published</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Traditional Ecological Knowledge and Climate Resilience in Eastern Nagaland
              </h2>
              <h3 className="text-xl font-semibold text-green-700 mb-6">
                Earthkeepers Project Report (2023–2025)
              </h3>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📅</span>
                  <div>
                    <p className="font-semibold text-gray-900">Publication Date</p>
                    <p className="text-gray-700">19 January 2026</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📍</span>
                  <div>
                    <p className="font-semibold text-gray-900">Research Location</p>
                    <p className="text-gray-700">Noklak and Meluri districts, Eastern Nagaland (Indo–Myanmar border)</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-2xl">🤝</span>
                  <div>
                    <p className="font-semibold text-gray-900">Support</p>
                    <p className="text-gray-700">International Development Research Centre (IDRC), Canada</p>
                  </div>
                </div>
              </div>

              <div className="prose prose-lg max-w-none mb-8">
                <p className="text-gray-700 leading-relaxed mb-4">
                  The Highland Institute has released a new report titled <strong><em>Traditional Ecological Knowledge and 
                  Climate Resilience in Eastern Nagaland</em></strong>, presenting findings from the Earthkeepers Project, 
                  a three-year collaborative research initiative carried out between 2023 and 2025. Supported by the International 
                  Development Research Centre (IDRC), Canada, the project worked closely with Indigenous communities in Noklak and 
                  Meluri districts along the Indo–Myanmar border.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  The report documents how communities understand and respond to climate change through long-standing relationships 
                  with land, forests, farming cycles, and water sources. Climate change is described through everyday experience, 
                  including declining crop yields, changes in sowing and harvest timings, delayed monsoons, unpredictable rainfall 
                  patterns, and increasing water scarcity. These changes are understood as local and lived realities rather than 
                  distant global processes.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  A central finding concerns the impact of border politics on climate vulnerability, where restrictions on movement 
                  and access to ancestral land have intensified environmental stress in a region dependent on mobility and shared 
                  ecological systems. At the same time, Indigenous institutions continue to play a key role in environmental care, 
                  collective farming, and local adaptation.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-6">
                  The report highlights capacity building that supported young Indo–Myanmar researchers to document community knowledge. 
                  It calls for climate and development policies that recognise Indigenous knowledge systems, reflect borderland realities, 
                  and support community-led approaches to conservation and climate resilience in Eastern Nagaland.
                </p>

                <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded mb-6">
                  <p className="font-semibold text-green-900 mb-2">Key Themes</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Traditional ecological knowledge and climate adaptation</li>
                    <li>Border politics and climate vulnerability</li>
                    <li>Indigenous institutions and environmental stewardship</li>
                    <li>Community-led climate resilience strategies</li>
                    <li>Capacity building for Indo–Myanmar researchers</li>
                  </ul>
                </div>

                <p className="text-gray-700 leading-relaxed italic">
                  Report published: 19 January 2026
                </p>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <p className="font-semibold text-gray-900 mb-3">For access to the report:</p>
                <div className="space-y-2">
                  <p className="text-gray-700 font-medium">Lanu Aier</p>
                  <p className="text-gray-600 text-sm">Operations Manager, Highland Institute</p>
                  <p className="flex items-center gap-2 text-gray-700 mt-2">
                    <span>📧</span>
                    <a href="mailto:lanu@highlandinstitute.org" className="text-green-600 hover:underline">
                      lanu@highlandinstitute.org
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">About Earthkeepers</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>Earthkeepers</strong> is a community-driven environmental education initiative funded by the 
                International Development Research Centre (IDRC) of Canada. The project focuses on building climate 
                resilience through intergenerational knowledge exchange in rural highland communities.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                At the heart of Earthkeepers is the recognition that indigenous and rural communities have been stewards 
                of biodiversity for generations, possessing sophisticated ecological knowledge passed down through oral 
                traditions, seasonal practices, and lived experience.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The project brings together children, youth, and elders to document traditional environmental knowledge, 
                share stories of ecological stewardship, and develop community-based responses to climate change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Activities */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What We Do</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Sprout className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Environmental Education</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Workshops and field activities connecting children with local ecosystems
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Intergenerational Exchange</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Bringing together elders and youth to share traditional ecological knowledge
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <BookOpen className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Storytelling</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Documenting oral histories, myths, and legends related to nature and seasons
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Climate Resilience</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Building adaptive capacity through community-led conservation initiatives
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Project Impact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-xl">Knowledge Documentation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Recording traditional ecological calendars, seasonal indicators, and indigenous conservation practices 
                    before they are lost to urbanization and cultural change.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-xl">Youth Empowerment</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Equipping young people with both traditional wisdom and contemporary environmental science, 
                    enabling them to become environmental leaders in their communities.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-xl">Community Resilience</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Strengthening social cohesion and collective environmental action through shared learning 
                    and collaborative conservation projects.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-xl">Policy Influence</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Elevating indigenous voices and traditional knowledge in regional and national climate 
                    adaptation policies and conservation strategies.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Supported By</h2>
            <div className="bg-white p-8 rounded-lg shadow-md inline-block">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                International Development Research Centre (IDRC)
              </h3>
              <p className="text-gray-600">Canada</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Initiatives */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Part of Our Broader Work</h2>
            <p className="text-gray-700 mb-6">
              Earthkeepers is part of the Highland Institute's comprehensive citizen science and biodiversity initiatives:
            </p>
            <ul className="space-y-4">
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Community biodiversity mapping and species documentation</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Participatory conservation initiatives and forest protection</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Training in data collection, GPS use, and wildlife tracking</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Integration of Traditional Ecological Knowledge (TEK) with modern science</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Get Involved</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Want to start an Earthkeepers project in your community or collaborate with our team?
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="mailto:info@highlandinstitute.org">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
              </Button>
            </a>
            <Link to="/research">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Explore All Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EarthkeepersPage;